import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useIntl } from "react-intl";

interface IMeta {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  image?: string;
  cover_image?: string;
}

const SITE_DOMAIN = "https://aversiclinic.ge";

export const useMeta = () => {
  const location = useLocation();
  const { formatMessage } = useIntl();

  const preTitle = formatMessage({
    id: "title",
    defaultMessage: "ავერსის კლინიკა",
  });

  const getCleanUrl = useCallback(() => {
    const path = location.pathname === "/" ? "" : location.pathname;
    return `${SITE_DOMAIN}${path}`;
  }, [location.pathname]);

  const setMetaTags = useCallback(
    (meta: IMeta) => {
      if (!meta) return;

      const title =
        meta?.title && meta.title !== preTitle
          ? `${meta.title} - ${preTitle}`
          : preTitle;

      const metaImage = meta?.image
        ? meta.image.startsWith("http")
          ? meta.image
          : `${process.env.REACT_APP_API_SERVER_URL}/storage/${meta.image}`
        : meta?.cover_image
        ? meta.cover_image.startsWith("http")
          ? meta.cover_image
          : `${process.env.REACT_APP_API_SERVER_URL}/storage/${meta.cover_image}`
        : "https://aversiclinic.ge/aversi.jpg";

      const finalUrl = meta?.canonicalUrl || getCleanUrl();

      document.title = title;

      updateOrCreateMetaTag("title", meta?.title || "");
      updateOrCreateMetaTag("description", meta?.description || "");

      updateOrCreateMetaProperty("og:title", meta?.title || "");
      updateOrCreateMetaProperty("og:description", meta?.description || "");
      updateOrCreateMetaProperty("og:url", finalUrl);
      updateOrCreateMetaProperty("og:image", metaImage);

      updateOrCreateMetaProperty("twitter:title", meta?.title || "");
      updateOrCreateMetaProperty("twitter:description", meta?.description || "");
      updateOrCreateMetaProperty("twitter:url", finalUrl);

      updateOrCreateCanonicalLinkTag(finalUrl);
    },
    [preTitle, getCleanUrl]
  );

  const updateOrCreateMetaTag = (name: string, content: string) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", name);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
  };

  const updateOrCreateMetaProperty = (property: string, content: string) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", property);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
  };

  const updateOrCreateCanonicalLinkTag = (href: string) => {
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement("link");
      tag.setAttribute("rel", "canonical");
      document.head.appendChild(tag);
    }
    tag.setAttribute("href", href);
  };

  return { setMetaTags };
};